/**
 * Matching a typed or ticketed name against a passenger profile.
 *
 * A profile holds a LEGAL name (`firstName` / `middleName` / `lastName`) and, optionally, a
 * `preferredName` — what the person is actually called. Both refer to the same human, and
 * different parts of the portal deliberately hold different ones:
 *
 *   • a trip's `travellerName` is written from the PREFERRED name, because that is who staff
 *     recognise on a trip list;
 *   • a flight is always ticketed in the LEGAL name, because an airline refuses travel when the
 *     ticket and the identity document disagree.
 *
 * ⚠️ So comparing the two with plain string equality concludes they are different people. That is
 * exactly how a traveller ended up added to their own trip a second time, as an additional
 * passenger, after a flight was booked for them — which then flowed into cost allocation and the
 * support worker report as though a second person had flown.
 *
 * These helpers live here, rather than in any one component, because three places need the same
 * rule and a copy that drifts is worse than no copy at all.
 */

export const normName = (s) => String(s || '').toLowerCase().replace(/\s+/g, ' ').trim();

/**
 * Every spelling of a profile's name we accept as referring to that person.
 *
 * The middle-name forms matter: a ticket is issued as first + middle + last, so a profile with a
 * middle name produces a ticketed name that none of the two-part spellings would ever match.
 */
export function passengerNameVariants(p) {
  const last   = p?.lastName   || '';
  const middle = p?.middleName || '';
  return [
    [p?.preferredName || p?.firstName, last],
    [p?.firstName,     last],
    [p?.preferredName, last],
    [p?.firstName,     middle, last],
    [p?.preferredName, middle, last],
  ].map(parts => normName(parts.filter(Boolean).join(' '))).filter(Boolean);
}

export function passengerMatchesName(p, name) {
  const n = normName(name);
  return !!n && passengerNameVariants(p).includes(n);
}

/**
 * Do a typed name and a profile disagree outright? Used to sanity-check the profile a
 * `travellerId` points at. An exact match isn't required — a linked account's name can differ a
 * little from the profile it belongs to — but sharing no name token at all means the id is
 * pointing at someone else entirely.
 */
export function passengerNameConflicts(p, name) {
  const typed = new Set(normName(name).split(' ').filter(Boolean));
  if (!typed.size) return false;
  const profile = [p?.firstName, p?.lastName, p?.preferredName, p?.middleName]
    .flatMap(v => normName(v).split(' '))
    .filter(Boolean);
  if (!profile.length) return false;
  return !profile.some(t => typed.has(t));
}

/**
 * The passenger profile a trip's traveller refers to.
 *
 * ⚠️ MATCHING ON THE USER LINK ALONE IS NOT ENOUGH, and getting this wrong is expensive. The
 * profile is where the booking flow draws the legal name, date of birth, gender, email, phone,
 * identity documents and frequent flyer numbers from. Miss it and the flight form silently falls
 * back to splitting the trip's DISPLAY name — which is the preferred name — so the traveller is
 * about to be ticketed as "Andy Hager" instead of "Andrew Hager", with every other field blank.
 * TripDetail looked up `where('userId','==',trip.travellerId)` and nothing else, so any traveller
 * whose profile was not linked to a portal login lost the lot.
 *
 * Order: the user link decides between two people who share a name; otherwise any accepted
 * spelling of the name wins; a link whose name flatly contradicts the trip is treated as stale.
 */
export function resolveTravellerProfile(trip, passengers = []) {
  const list  = passengers || [];
  const typed = normName(trip?.travellerName);
  const byId  = trip?.travellerId ? list.find(p => p.userId === trip.travellerId) : null;

  // No name to go on — the link is all there is, and it is better than nothing.
  if (!typed) return byId || null;

  const byName = list.find(p => passengerMatchesName(p, typed));
  if (byName) return (byId && passengerMatchesName(byId, typed)) ? byId : byName;

  // No profile is named what the trip says. Fall back to the link unless it points somewhere
  // else entirely, which means the id is stale rather than merely spelled differently.
  return (byId && !passengerNameConflicts(byId, typed)) ? byId : null;
}
